import { z } from "zod";
import {
  VOTE_QUORUM_MIN_KEY,
  VOTE_WEIGHT_SOURCE_KEY,
  voteQuorumMinSchema,
  voteWeightSourceSchema,
} from "./vote-weight-config.js";
import {
  COOLING_THRESHOLD_MINOR_KEY,
  COOLING_WINDOW_MINUTES_KEY,
  coolingThresholdMinorPatchSchema,
  coolingWindowMinutesPatchSchema,
} from "./cooling-freeze-anomaly.js";
import {
  EXPENSE_AUTO_APPROVE_MINOR_KEY,
  EXPENSE_AUTO_MODE_ENABLED_KEY,
  EXPENSE_VOTE_REQUIRED_MINOR_KEY,
  decisionThresholdMinorPatchSchema,
  expenseAutoModeEnabledPatchSchema,
} from "./decision-mode-config.js";
import { governanceConfigSchema } from "./admin-effective-config.js";

/**
 * Shared DTO — Admin GHI ĐÈ config quỹ per-fund (US-AIF.P4-0258 · @UC9451). MUTATION.
 *
 * Mở rộng 0244 (effective-config READ-ONLY) sang GHI: admin SUPER_ADMIN/OPS ghi đè luật vote /
 * ngưỡng duyệt chi / cooling của MỘT quỹ (trước đây chỉ owner sửa). Ghi vào tầng `Fund.settings`
 * (jsonb) — đúng tầng mà resolver fund>group>system ĐANG đọc ⇒ override có HIỆU LỰC THẬT.
 *
 * 🔴 CHỐNG TOGGLE NO-OP (bài học 0084/PR#172 — "config nói dối về tiền"): CHỈ 7 khoá dưới đây được
 * phơi ra, mỗi khoá có CONSUMER THẬT trong engine (đã grep, có file:line):
 *  - `vote_weight_source` / `vote_quorum_min`
 *      → ExpenseDecisionService.openVote (expense-decision.service.ts:735) SNAPSHOT vào consensus.
 *  - `cooling_threshold_minor` / `cooling_window_minutes`
 *      → ExpenseDecisionService.approve (expense-decision.service.ts:379) isCoolingRequired ⇒ hoãn/post.
 *  - `expense_auto_mode_enabled` / `expense_auto_approve_threshold_minor` /
 *    `expense_vote_required_threshold_minor`
 *      → ExpenseDecisionService._resolveMode (expense-decision.service.ts:215) resolveDecisionMode.
 * KHÔNG thêm khoá "để dành" — khoá không có engine đọc = toggle nói dối, cấm phơi.
 *
 * INVARIANTS:
 *  - `.strict()` — field lạ ⇒ 400 tại biên (không ghi rác vào jsonb).
 *  - `reason` BẮT BUỘC, non-blank (audit trail admin can thiệp quỹ NGƯỜI DÙNG — tiền lệ 0160 C1
 *    takedown-note + adminInterveneSplitSchema). Thiếu/blank ⇒ 400, KHÔNG ghi row, KHÔNG audit.
 *  - ≥1 khoá override có mặt (patch rỗng = gọi sai ⇒ 400).
 *  - Tiền = CHUỖI BigInt minor (B1 no-float) — reuse guard domain, KHÔNG z.number().
 *  - `null` = XOÁ override khỏi tầng fund (rơi về group/system) — KHÔNG lưu null rác vào jsonb.
 *  - Validator DERIVE từ contract sẵn có (vote-weight/cooling/decision-mode) ⇒ 1 nguồn sự thật,
 *    admin KHÔNG ghi được giá trị mà owner-path/resolver coi là bẩn.
 */

/** Danh sách khoá override được phép (thứ tự ổn định cho UI/test). MỖI khoá có engine consumer. */
export const ADMIN_OVERRIDE_CONFIG_KEY_LIST = [
  VOTE_WEIGHT_SOURCE_KEY,
  VOTE_QUORUM_MIN_KEY,
  COOLING_THRESHOLD_MINOR_KEY,
  COOLING_WINDOW_MINUTES_KEY,
  EXPENSE_AUTO_MODE_ENABLED_KEY,
  EXPENSE_AUTO_APPROVE_MINOR_KEY,
  EXPENSE_VOTE_REQUIRED_MINOR_KEY,
] as const;

export type AdminOverrideConfigKey = (typeof ADMIN_OVERRIDE_CONFIG_KEY_LIST)[number];

/** Lý do can thiệp — BẮT BUỘC, non-blank, đủ dài để có nghĩa khi audit đọc lại. */
export const overrideReasonSchema = z
  .string()
  .trim()
  .min(10, { message: "Lý do ghi đè phải có ít nhất 10 ký tự (bắt buộc để audit)." })
  .max(500, { message: "Lý do ghi đè tối đa 500 ký tự." });

/** `T | null` — null = XOÁ override tầng quỹ (rơi về group/system). */
function overridable<T extends z.ZodTypeAny>(schema: T) {
  return schema.nullable().optional();
}

/**
 * Body PATCH /admin/funds/:id/config-override (@UC9451).
 * ≥1 khoá + `reason` bắt buộc. Kiểm chéo ngưỡng auto ≤ voteRequired khi CẢ HAI có mặt (mirror
 * `fundDecisionThresholdPatchSchema` — KHÔNG nới lỏng bất biến của owner-path).
 */
export const adminOverrideConfigPatchSchema = z
  .object({
    reason: overrideReasonSchema,
    voteWeightSource: overridable(voteWeightSourceSchema),
    voteQuorumMin: overridable(voteQuorumMinSchema),
    coolingThresholdMinor: overridable(coolingThresholdMinorPatchSchema),
    coolingWindowMinutes: overridable(coolingWindowMinutesPatchSchema),
    expenseAutoModeEnabled: overridable(expenseAutoModeEnabledPatchSchema),
    expenseAutoApproveThresholdMinor: overridable(decisionThresholdMinorPatchSchema),
    expenseVoteRequiredThresholdMinor: overridable(decisionThresholdMinorPatchSchema),
  })
  .strict()
  .refine(
    (body) => Object.keys(body).some((k) => k !== "reason"),
    { message: "Cần ít nhất 1 thiết lập để ghi đè." },
  )
  .superRefine((body, ctx) => {
    const auto = body.expenseAutoApproveThresholdMinor;
    const vote = body.expenseVoteRequiredThresholdMinor;
    // Chỉ kiểm khi CẢ HAI cùng đặt giá trị (null = xoá ⇒ không so sánh).
    if (auto == null || vote == null) return;
    if (BigInt(auto) > BigInt(vote)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expenseAutoApproveThresholdMinor"],
        message: "Ngưỡng tự duyệt phải ≤ ngưỡng bắt buộc biểu quyết",
      });
    }
  });

export type AdminOverrideConfigPatch = z.infer<typeof adminOverrideConfigPatchSchema>;

/** Response PATCH override — governance HIỆU LỰC SAU ghi (admin thấy ngay kết quả thật). */
export const adminOverrideConfigResponseSchema = z
  .object({
    fundId: z.string(),
    governance: governanceConfigSchema,
  })
  .strict();
export type AdminOverrideConfigResponse = z.infer<typeof adminOverrideConfigResponseSchema>;
